import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TRACKS = ["Semiconductor Design", "Advanced Materials", "Application Development with Indigenous Chips"];

const EMPTY = {
  entity_name: "", entity_type: "", registration: "", website: "", contact_name: "", email: "", phone: "",
  project_title: "", track: "", grant_requested_lakh: "", duration_months: "", current_trl: "", target_trl: "",
  indigenous_chip: "", value_proposition: "", problem: "", technology: "", validation_plan: "", equity_consent: false,
};

const inputCls = "w-full bg-white border border-ink/15 focus:border-teal outline-none px-4 py-3 text-ink text-sm";

export default function ChipmatForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");
  const entityTypes = t("chipmat.entity_types", { returnObjects: true });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equity_consent) { toast.error(t("chipmat.equity_required")); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        grant_requested_lakh: num(form.grant_requested_lakh),
        duration_months: num(form.duration_months),
        current_trl: num(form.current_trl),
        target_trl: num(form.target_trl),
      };
      const r = await api.post("/chipmat/applications", payload);
      setReference(r.data.reference);
      toast.success(t("chipmat.success", { ref: r.data.reference }));
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("chipmat.fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="chipmat-form" aria-label={t("chipmat.form_title")}>
      {reference && (
        <div data-testid="chipmat-success" role="status" className="border border-teal bg-teal-light text-teal-dark p-4 text-sm">
          {t("chipmat.success", { ref: reference })}
        </div>
      )}

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("chipmat.sec_applicant")}</legend>
        <Field label={t("chipmat.entity_name")}><input data-testid="cm-entity-name" required value={form.entity_name} onChange={set("entity_name")} className={inputCls} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("chipmat.entity_type")}>
            <select data-testid="cm-entity-type" required value={form.entity_type} onChange={set("entity_type")} className={inputCls}>
              <option value="">—</option>
              {entityTypes.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label={t("chipmat.registration")}><input data-testid="cm-registration" value={form.registration} onChange={set("registration")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t("chipmat.contact_name")}><input data-testid="cm-contact-name" required value={form.contact_name} onChange={set("contact_name")} className={inputCls} /></Field>
          <Field label={t("chipmat.email")}><input data-testid="cm-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label={t("chipmat.phone")}><input data-testid="cm-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
        </div>
        <Field label={t("chipmat.website")}><input data-testid="cm-website" value={form.website} onChange={set("website")} className={inputCls} placeholder="https://" /></Field>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("chipmat.sec_project")}</legend>
        <Field label={t("chipmat.project_title")}><input data-testid="cm-project-title" required value={form.project_title} onChange={set("project_title")} className={inputCls} /></Field>
        <Field label={t("chipmat.track")}>
          <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label={t("chipmat.track")}>
            {TRACKS.map((tr, i) => (
              <label key={tr} htmlFor={`cm-track-${i + 1}`} className={`border px-4 py-3 text-sm cursor-pointer transition-colors ${form.track === tr ? "border-teal bg-teal-light text-teal-dark" : "border-ink/15 text-ink/80 hover:border-teal"}`}>
                <input type="radio" name="track" id={`cm-track-${i + 1}`} data-testid={`cm-track-${i + 1}`} value={tr} checked={form.track === tr} onChange={set("track")} className="sr-only" required />
                {tr}
              </label>
            ))}
          </div>
        </Field>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label={t("chipmat.grant")}><input data-testid="cm-grant" type="number" min="1" max="40" step="0.5" value={form.grant_requested_lakh} onChange={set("grant_requested_lakh")} className={inputCls} /></Field>
          <Field label={t("chipmat.duration")}><input data-testid="cm-duration" type="number" min="3" max="24" value={form.duration_months} onChange={set("duration_months")} className={inputCls} /></Field>
          <Field label={t("chipmat.trl_now")}><input data-testid="cm-trl-now" type="number" min="1" max="9" value={form.current_trl} onChange={set("current_trl")} className={inputCls} /></Field>
          <Field label={t("chipmat.trl_target")}><input data-testid="cm-trl-target" type="number" min="1" max="9" value={form.target_trl} onChange={set("target_trl")} className={inputCls} /></Field>
        </div>
        {form.track === TRACKS[2] && (
          <Field label={t("chipmat.indigenous_chip")}><input data-testid="cm-indigenous-chip" value={form.indigenous_chip} onChange={set("indigenous_chip")} className={inputCls} /></Field>
        )}
        <Field label={t("chipmat.value_prop")}><input data-testid="cm-value-prop" required maxLength={300} value={form.value_proposition} onChange={set("value_proposition")} className={inputCls} /></Field>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("chipmat.sec_tech")}</legend>
        <Field label={t("chipmat.problem")}><textarea data-testid="cm-problem" rows={3} value={form.problem} onChange={set("problem")} className={`${inputCls} resize-none`} /></Field>
        <Field label={t("chipmat.technology")}><textarea data-testid="cm-technology" rows={3} value={form.technology} onChange={set("technology")} className={`${inputCls} resize-none`} /></Field>
        <Field label={t("chipmat.validation")}><textarea data-testid="cm-validation" rows={3} value={form.validation_plan} onChange={set("validation_plan")} className={`${inputCls} resize-none`} /></Field>
      </fieldset>

      <label className="flex items-start gap-3 border border-amber/60 bg-amber/10 p-4 cursor-pointer">
        <input type="checkbox" data-testid="cm-equity" checked={form.equity_consent} onChange={set("equity_consent")} className="mt-1 accent-[#00886A] w-4 h-4" />
        <span className="text-sm text-ink/85 leading-relaxed">{t("chipmat.equity")}</span>
      </label>

      <button type="submit" data-testid="cm-submit" disabled={submitting} className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-white py-4 font-medium transition-colors">
        {submitting ? t("chipmat.submitting") : t("chipmat.submit")}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
